import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Moon, Play, Pause, X, BookOpen, Volume2, ChevronRight, Loader2 } from 'lucide-react';
import GuidedHypnosis from './GuidedHypnosis';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Format secs as m:ss */
const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/* =========================================================
   HypnosPlayer — guides through phases, optionally with TTS
   ========================================================= */
function HypnosPlayer({ script, mode, onClose, onComplete }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [remaining, setRemaining] = useState(script.phases[0].duration_sec);
  const [playing, setPlaying] = useState(false);
  const [audioCache, setAudioCache] = useState({});
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioRef = useRef(null);
  const tickRef = useRef(null);

  const phase = script.phases[phaseIdx];
  const totalSec = script.phases.reduce((s, p) => s + p.duration_sec, 0);

  // Tick countdown
  useEffect(() => {
    if (!playing) return;
    tickRef.current = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(tickRef.current);
  }, [playing, phaseIdx]);

  // Auto-advance
  useEffect(() => {
    if (!playing || remaining > 0) return;
    if (phaseIdx < script.phases.length - 1) {
      const next = phaseIdx + 1;
      setPhaseIdx(next);
      setRemaining(script.phases[next].duration_sec);
    } else {
      setPlaying(false);
      onComplete?.();
    }
     
  }, [remaining, playing]);

  // Load TTS for current phase if mode=audio
  useEffect(() => {
    if (mode !== 'audio' || !playing) return;
    if (audioCache[phaseIdx]) {
      const a = new Audio(audioCache[phaseIdx]);
      audioRef.current = a;
      a.play().catch(() => {});
      return;
    }
    setLoadingAudio(true);
    axios.post(
      `${API}/reader/tts`,
      { text: phase.text, voice: 'shimmer', model: 'tts-1' },
      { withCredentials: true, responseType: 'blob' },
    ).then((res) => {
      const url = URL.createObjectURL(res.data);
      setAudioCache((prev) => ({ ...prev, [phaseIdx]: url }));
      const a = new Audio(url);
      audioRef.current = a;
      a.play().catch(() => {});
    }).catch(() => {
      toast.error('Kunde inte ladda ljud — fortsätt läsa själv');
    }).finally(() => setLoadingAudio(false));
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
     
  }, [phaseIdx, playing, mode]);

  // Cleanup audio on close/unmount
  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    Object.values(audioCache).forEach((u) => URL.revokeObjectURL(u));
  }, []);  

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      if (audioRef.current) audioRef.current.pause();
    } else {
      setPlaying(true);
      if (audioRef.current && mode === 'audio') audioRef.current.play().catch(() => {});
    }
  };

  const skip = () => {
    if (audioRef.current) audioRef.current.pause();
    if (phaseIdx < script.phases.length - 1) {
      const next = phaseIdx + 1;
      setPhaseIdx(next);
      setRemaining(script.phases[next].duration_sec);
    } else {
      setPlaying(false);
      onComplete?.();
    }
  };

  const elapsedTotal = script.phases.slice(0, phaseIdx).reduce((s, p) => s + p.duration_sec, 0) + (phase.duration_sec - remaining);
  const totalPct = Math.round((elapsedTotal / totalSec) * 100);

  return (
    <div className="fixed inset-0 z-[220] overflow-auto" style={{ background: `linear-gradient(180deg, ${script.color}55, white 40%)` }} data-testid="hypnos-player">
      <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b-[3px] border-black p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl">{script.emoji}</span>
          <div className="min-w-0">
            <div className="font-black text-sm leading-tight truncate">{script.title}</div>
            <div className="text-[10px] opacity-60">{mode === 'audio' ? '🔊 Guidad audio' : '📖 Skript att läsa'}</div>
          </div>
        </div>
        <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="hypnos-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 pt-3">
        <div className="h-2 w-full border-[2px] border-black rounded-full overflow-hidden bg-white">
          <div className="h-full transition-all duration-500" style={{ width: `${totalPct}%`, background: 'linear-gradient(90deg, #FFC8DD, #C8B6FF, #A2D2FF)' }} />
        </div>
        <div className="text-[10px] text-center opacity-60 mt-1">
          Fas {phaseIdx + 1} / {script.phases.length} · {fmt(elapsedTotal)} / {fmt(totalSec)}
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 pb-24">
        <div className="neo-card p-5 bg-white" data-testid="hypnos-phase">
          <div className="text-center mb-3">
            <div className="text-5xl mb-2">{phase.emoji}</div>
            <h3 className="font-black text-xl">{phase.name}</h3>
            <div className="w-20 h-20 mx-auto mt-3 rounded-full border-[4px] border-black flex items-center justify-center bg-gradient-to-br from-white to-[#FFE8F5] shadow-[3px_3px_0_#000]">
              <span className="text-2xl font-black font-mono">{fmt(remaining)}</span>
            </div>
          </div>

          <div className="border-l-[3px] border-black pl-3 my-4 max-h-[40vh] overflow-y-auto" data-testid="hypnos-text">
            <p className="text-sm leading-relaxed italic whitespace-pre-line">{phase.text}</p>
          </div>

          <div className="flex justify-center gap-2 pt-2 items-center">
            <button onClick={togglePlay} disabled={loadingAudio} className="neo-btn px-5 py-3 bg-[#C8B6FF] font-bold flex items-center gap-2" data-testid="hypnos-play">
              {loadingAudio ? <><Loader2 className="w-4 h-4 animate-spin" /> Laddar...</> : playing ? <><Pause className="w-4 h-4" /> Paus</> : <><Play className="w-4 h-4" /> {phaseIdx === 0 && remaining === phase.duration_sec ? 'Starta' : 'Fortsätt'}</>}
            </button>
            <button onClick={skip} className="neo-btn px-3 py-3 bg-white text-xs flex items-center gap-1" data-testid="hypnos-skip">
              <ChevronRight className="w-4 h-4" /> Nästa fas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   HypnosTab main
   ========================================================= */
export default function HypnosTab() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState('read');
  const [intention, setIntention] = useState('');
  const [showPlayer, setShowPlayer] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/hypnos/scripts`, { withCredentials: true }),
      axios.get(`${API}/hypnos/sessions`, { withCredentials: true }),
    ]).then(([s, ss]) => {
      setScripts(s.data?.scripts || []);
      setSessions(ss.data?.sessions || []);
    }).finally(() => setLoading(false));
  }, []);

  const begin = (script, m) => {
    setActive(script);
    setMode(m);
    setShowPlayer(true);
  };

  const onComplete = async () => {
    if (!active) return;
    try {
      await axios.post(`${API}/hypnos/sessions`, {
        script_id: active.id,
        mode,
        intention: intention || null,
        length_min: active.duration_min,
      }, { withCredentials: true });
      const { data } = await axios.get(`${API}/hypnos/sessions`, { withCredentials: true });
      setSessions(data?.sessions || []);
      toast.success(`🌸 ${active.duration_min} min med ${active.title}`);
    } catch { /* ignore */ }
    setShowPlayer(false); setIntention('');
  };

  if (loading) return <div className="text-center py-8 text-sm opacity-60">Laddar...</div>;

  return (
    <div className="space-y-4 pb-6" data-testid="hypnos-tab">
      <GuidedHypnosis />
      <div className="neo-card p-5 bg-gradient-to-br from-[#C8B6FF] via-[#FFE8F5] to-[#A2D2FF]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white border-2 border-black flex items-center justify-center">
            <Moon className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-['Fredoka']">Hypnos 🌙</h2>
            <p className="text-xs text-gray-700">Mjuka, korta resor inåt — skript på svenska eller AI-guidad audio</p>
          </div>
        </div>
        <p className="text-[11px] opacity-70 italic mt-2">
          Inget magi. Bara djupandning + suggestioner som hjärnan älskar att repetera. Stäng ögon, andas, lyssna.
        </p>
      </div>

      {!active && (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="hypnos-script-list">
          {scripts.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s)}
              className="neo-card p-4 text-left hover:scale-[1.02] transition"
              style={{ background: `linear-gradient(135deg, ${s.color}88, white)` }}
              data-testid={`hypnos-script-${s.id}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-4xl">{s.emoji}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-black text-base leading-tight">{s.title}</h3>
                  <p className="text-[11px] opacity-70 mt-0.5">{s.subtitle}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] bg-white border border-black rounded-full px-1.5 py-0.5">{s.duration_min} min</span>
                    <span className="text-[10px] opacity-60">{s.phases.length} faser</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {active && !showPlayer && (
        <div className="neo-card p-5 bg-white" data-testid="hypnos-prep">
          <button onClick={() => setActive(null)} className="text-xs underline opacity-60 mb-2">← Tillbaka</button>
          <div className="text-center mb-4">
            <div className="text-5xl">{active.emoji}</div>
            <h3 className="font-black text-xl mt-2">{active.title}</h3>
            <p className="text-xs opacity-70">{active.subtitle} · {active.duration_min} min</p>
          </div>
          {active.intent_hint && (
            <div className="mb-3">
              <label className="text-xs font-bold block mb-1">Din intention (valfri)</label>
              <input
                type="text"
                value={intention}
                onChange={(e) => setIntention(e.target.value)}
                placeholder={active.intent_hint}
                className="neo-input w-full text-sm"
                maxLength={200}
                data-testid="hypnos-intention"
              />
            </div>
          )}
          <p className="text-xs opacity-70 italic mb-3">Välj hur du vill uppleva:</p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => begin(active, 'read')} className="neo-btn p-3 bg-[#FFE8A3] flex flex-col items-center gap-1 text-sm font-bold" data-testid="hypnos-mode-read">
              <BookOpen className="w-5 h-5" />
              Skript att läsa
              <span className="text-[10px] opacity-60 font-normal">Du läser tyst för dig själv</span>
            </button>
            <button onClick={() => begin(active, 'audio')} className="neo-btn p-3 bg-[#C8B6FF] flex flex-col items-center gap-1 text-sm font-bold" data-testid="hypnos-mode-audio">
              <Volume2 className="w-5 h-5" />
              AI-audio
              <span className="text-[10px] opacity-60 font-normal">Mjuk röst leder dig</span>
            </button>
          </div>
          <p className="text-[10px] opacity-50 italic text-center mt-2">
            Audio använder Emergent LLM-key — kostnaden är liten per session.
          </p>
        </div>
      )}

      {showPlayer && active && (
        <HypnosPlayer
          script={active}
          mode={mode}
          onClose={() => setShowPlayer(false)}
          onComplete={onComplete}
        />
      )}

      {sessions.length > 0 && !active && (
        <div className="neo-card p-3 bg-white" data-testid="hypnos-history">
          <h3 className="font-bold text-sm mb-2">🗂️ Senaste sessioner</h3>
          <div className="space-y-1">
            {sessions.slice(0, 5).map((s) => {
              const sc = scripts.find((x) => x.id === s.script_id);
              return (
                <div key={s.id} className="flex items-center gap-2 text-xs border-l-2 border-black pl-2 py-1">
                  <span className="text-lg">{sc?.emoji || '🌙'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{sc?.title || s.script_id}</div>
                    <div className="text-[10px] opacity-60">
                      {new Date(s.created_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} · {s.mode === 'audio' ? '🔊' : '📖'} · {s.length_min || '?'} min
                      {s.intention && ` · "${s.intention}"`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
